import { Component, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { MenuAuditLogService } from './features/menu-audit-log/services/menu-audit-log.service';
import { HeaderComponent } from './layout/header.component';
import { LayoutPreferencesService } from './layout/layout-preferences.service';
import { SidebarComponent } from './layout/sidebar.component';
import { ToastHostComponent } from './shared/ui/toast-host.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, ToastHostComponent],
  styles: [
    `
      :host {
        --top-drawer-width-expanded: min(272px, calc(100vw - 0.75rem));
        --top-drawer-width-compact: 56px;
      }

      .app-main-top-drawer-expanded {
        margin-left: var(--top-drawer-width-expanded);
        width: calc(100% - var(--top-drawer-width-expanded));
        max-width: calc(100% - var(--top-drawer-width-expanded));
        transition:
          margin-left 0.22s ease,
          width 0.22s ease;
      }

      .app-main-top-drawer-compact {
        margin-left: var(--top-drawer-width-compact);
        width: calc(100% - var(--top-drawer-width-compact));
        max-width: calc(100% - var(--top-drawer-width-compact));
      }

      .top-drawer-shell {
        position: fixed;
        top: 46px;
        left: 0;
        bottom: 0;
        z-index: 1037;
        width: var(--top-drawer-width-expanded);
        box-shadow: 18px 0 44px rgba(7, 24, 56, 0.2);
        transition: width 0.22s ease;
      }

      .top-drawer-shell-compact {
        width: var(--top-drawer-width-compact);
      }

      .top-drawer-shell app-sidebar {
        display: block;
        height: 100%;
      }

      @media (max-width: 575.98px) {
        :host {
          --top-drawer-width-expanded: calc(100vw - 0.5rem);
          --top-drawer-width-compact: 56px;
        }

        .top-drawer-shell {
          top: 44px;
        }

        .app-main-top-drawer-expanded,
        .app-main-top-drawer-compact {
          margin-left: 0;
          width: 100%;
          max-width: 100%;
        }
      }
    `,
  ],
  template: `
    <app-toast-host />
    @if (auth.loading() && router.url !== '/login') {
      <div class="container py-5">Loading...</div>
    } @else if (auth.isAuthenticated() && router.url !== '/login') {
      <div
        class="app-shell"
        [class.app-shell-left]="layout.navigationMode() === 'left'"
        [class.app-shell-top]="layout.navigationMode() === 'top'"
      >
        @if (layout.navigationMode() === 'left') {
          <app-sidebar [mode]="'left'" [collapsed]="layout.sidebarCollapsed()" />
        }
        <div
          class="app-main"
          [class.app-main-left]="layout.navigationMode() === 'left'"
          [class.app-main-left-collapsed]="layout.navigationMode() === 'left' && layout.sidebarCollapsed()"
          [class.app-main-top]="layout.navigationMode() === 'top'"
          [class.app-main-top-drawer-expanded]="layout.navigationMode() === 'top' && layout.topDrawerOpen() && !layout.topDrawerCompact()"
          [class.app-main-top-drawer-compact]="layout.navigationMode() === 'top' && layout.topDrawerOpen() && layout.topDrawerCompact()"
        >
          <app-header />
          @if (layout.navigationMode() === 'top' && !layout.topDrawerOpen()) {
            <app-sidebar [mode]="'top'" />
          }
          <router-outlet />
        </div>

        @if (layout.navigationMode() === 'top' && layout.topDrawerOpen()) {
          <div class="top-drawer-shell" [class.top-drawer-shell-compact]="layout.topDrawerCompact()">
            <app-sidebar [mode]="'left'" [drawer]="true" />
          </div>
        }
      </div>
    } @else {
      <router-outlet />
    }
  `,
})
export class AppComponent implements OnInit {
  protected readonly auth = inject(AuthService);
  protected readonly router = inject(Router);
  protected readonly layout = inject(LayoutPreferencesService);
  private readonly auditLogs = inject(MenuAuditLogService);

  async ngOnInit(): Promise<void> {
    if (this.router.url === '/login') return;
    await this.auth.hydrate();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (!this.auth.isAuthenticated() || event.urlAfterRedirects === '/login') return;
        const path = event.urlAfterRedirects || '/';
        this.auditLogs
          .logEvent({
            module_name: this.routeModuleName(path),
            entity_type: 'page',
            entity_id: path,
            entity_name: this.routeEntityName(path),
            action: 'VIEW',
            new_data: { path },
          })
          .subscribe({ error: () => undefined });
      });
  }

  private routeModuleName(path: string): string {
    const normalized = (path || '/').replace(/^\//, '').split('/')[0] || 'dashboard';
    return normalized
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private routeEntityName(path: string): string {
    if (path === '/') return 'Dashboard';
    return path;
  }
}
